import * as React from 'react';
import { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SignInResponse from '../model/login/SignInResponse';
import SignInService from '../services/SignInService';
import signInStatus from '../enums/signInStatus';
import config from './../config/app';

type AuthenticationProviderProps = {
    children: React.ReactNode;
};

export type IAuthInfo = SignInResponse & {
    isAdmin: boolean;
};

const defaultState: IAuthenticationContext = {
    authInfo: {
        username: '',
        isAdmin: false,
        fullName: '',
        firstName: '',
        lastName: '',
    },
    login: (_username: string, _password: string) => new Promise<SignInResponse>(() => {}),
    logout: () => new Promise<void>(() => {}),
    clearAuthInfo: () => {},
};

const AuthenticationContext = React.createContext<IAuthenticationContext>(defaultState);

export type IAuthenticationContext = {
    authInfo: IAuthInfo;
    login: (username: string, password: string) => Promise<SignInResponse>;
    logout: () => Promise<void>;
    clearAuthInfo: () => void;
};

export const useAuthentication = () => {
    return useContext(AuthenticationContext);
};

const resolveAdminStatus = async (status?: string): Promise<boolean> => {
    if (status !== signInStatus.Success) {
        return false;
    }
    return SignInService.checkAdminAccess();
};

export const AuthenticationProvider = ({ children }: AuthenticationProviderProps) => {
    let navigate = useNavigate();
    const [authInfo, setAuthInfo] = useState<IAuthInfo>({ isAdmin: false });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        SignInService.getStatus().then(async (response) => {
            const isAdmin = await resolveAdminStatus(response.status);
            setAuthInfo({ ...response, isAdmin });
            setIsLoading(false);
        });
    }, []);

    function login(username: string, password: string) {
        return SignInService.signIn(username, password).then(async (response) => {
            const isAdmin = await resolveAdminStatus(response.status);
            const authData = { ...response, isAdmin };
            setAuthInfo(authData);
            return response;
        });
    }

    function logout() {
        return SignInService.signOut().then(() => {
            clearAuthInfo();
            navigate(config.loginPath);
        });
    }

    function clearAuthInfo() {
        setAuthInfo(defaultState.authInfo);
    }

    const value = {
        authInfo,
        login,
        logout,
        clearAuthInfo,
    };

    return <AuthenticationContext.Provider value={value}>{!isLoading && children}</AuthenticationContext.Provider>;
};
